import React, { useEffect, useState } from "react";
import Main from "../Components/Main";
import PageContainer from "../Components/PageContainer";
import { useToast } from "../Components/toast/ToastProvider";
import { getVehicleAdvanceByDate } from "../Service/VehicleAdvanceService";
import { getAllVehicles } from "../Service/VehicleService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import VehicleAdvanceFilter from "../Components/VehicleAdvanceFilter";
import CollapseCard from "../Components/CollapseCard";
import { Button, Table } from "react-bootstrap";
import { Download } from "react-feather";
import { formatDate } from "../config/function";

const VehicleAdvanceReport = () => {
  const [vehicles, setVehicles] = useState([]);
  const [filtered, setFiltered] = useState({
    date: "",
  });
  const [filteredVehicle, setFilteredVehicle] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchVehicles();
  }, []);
  const fetchVehicles = async () => {
    try {
      const res = await getAllVehicles();
      setVehicles(res.data || []);
    } catch (err) {
      toast(err.message || "Failed to fetch vehicles", "ERROR");
    }
  };

  const handleFilterChange = (name, value) => {
    setFiltered((prev) => ({ ...prev, [name]: value }));
  };

  const handleFind = async () => {
    if (!filtered.date) {
      toast("Please select a date", "ERROR");
      return;
    }
    setLoading(true);
    try {
      const res = await getVehicleAdvanceByDate(filtered.date);

      const data = res.data || [];
      const filteredData = data.filter((s) => s.status === "open");
      if (data.filteredData === 0) {
        toast("No records found", "ERROR");
      }
      setTimeout(() => {
        setFilteredVehicle(filteredData);
        setLoading(false);
      }, 1500);
    } catch (err) {
      toast(err.message || "Failed to fetch vehicle advance", "ERROR");
    }
  };

  const handleReset = () => {
    setFiltered({ vehicle_id: "", date: "" });
    setFilteredVehicle([]);
  };

  const addPDFHeader = (doc, pageWidth) => {
    const logo = new Image();
    logo.src = "/img/asa_logo.png";

    const logoWidth = 18;
    const logoHeight = 18;
    const spacing = 5;

    const headerText = "ASA Transport";
    const subHeaderText = "Mannargudi";

    const headerWidth = doc.getTextWidth(headerText);
    const subHeaderWidth = doc.getTextWidth(subHeaderText);
    const totalWidth =
      logoWidth + spacing + Math.max(headerWidth, subHeaderWidth);
    const startX = (pageWidth - totalWidth) / 2;

    try {
      doc.addImage(logo, "PNG", startX, 8, logoWidth, logoHeight);
    } catch (e) {
      console.warn("Logo load skipped (small size optimization)");
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(headerText, startX + logoWidth + spacing, 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(subHeaderText, startX + logoWidth + spacing, 20);
  };

  const generateTablePDF = () => {
    if (filteredVehicle.length === 0) {
      toast("No trips to export", "ERROR");
      return;
    }
    const doc = new jsPDF({ orientation: "portrait", compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();

    addPDFHeader(doc, pageWidth);

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Vehicle Advance Report", pageWidth / 2, 35, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Date:${formatDate(filtered.date) || "N/A"}`, 20, 47);

    let y = 50;
    const tableColumn = ["S.No", "Vehicle", "Total Amount"];
    const tableRows = filteredVehicle.map((trip, index) => {
      const vehicle = vehicles.find((v) => v.id === trip.vehicle_id);
      return [
        index + 1,
        vehicle?.vehicle_number || "N/A",
        trip.amount.toFixed(2) || 0.0,
      ];
    });

    autoTable(doc, {
      startY: y + 10,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        fontStyle: "bold",
      },
    });

    const pdfOutput = doc.output("arraybuffer");
    const fileSizeKB = pdfOutput.byteLength / 1024;
    const fileSizeText =
      fileSizeKB > 1024
        ? `${(fileSizeKB / 1024).toFixed(2)} MB`
        : `${fileSizeKB.toFixed(2)} KB`;

    const fileName = `Vehicle_Advance_Report_.pdf`;
    doc.save(fileName);
  };

  return (
    <>
      <Main>
        <PageContainer>
          <CollapseCard title="vehicle Advance Filter">
            <VehicleAdvanceFilter
              filters={filtered}
              onFilterChange={handleFilterChange}
              onFind={handleFind}
              onReset={handleReset}
              vehicles={vehicles}
              loading={loading}
            />
          </CollapseCard>

          <CollapseCard title="Vehicle Advance">
            <Table size="sm">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Vehicle Number</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredVehicle.length > 0 ? (
                  filteredVehicle.map((item, index) => {
                    const vehicle = vehicles.find(
                      (v) => v.id === item.vehicle_id,
                    );
                    return (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>{vehicle?.vehicle_number || "-"}</td>
                        <td>{item.amount.toFixed(2)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No vehicle Advance data found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </CollapseCard>
          <div className="d-flex justify-content-end mb-2 mt-2">
            <Button variant="success" size="sm" onClick={generateTablePDF}>
              <Download /> Download PDF
            </Button>
          </div>
        </PageContainer>
      </Main>
    </>
  );
};

export default VehicleAdvanceReport;
